import { productPageGetAttributeSelectionEntryConditions } from '../ProductPageAttributesSelection/ProductPageAttributeSelectionGroupRenderer/ProductPageAttributeSelector/productPageGetAttributeSelectionEntryConditions'

import {
  ATTRIBUTE_NAME_ADDITION,
  ATTRIBUTE_NAME_ANIMAL_SIZE,
  ATTRIBUTE_NAME_CANNULA_LENGTH,
  ATTRIBUTE_NAME_CBD_CONTENT,
  ATTRIBUTE_NAME_CONSISTENCY,
  ATTRIBUTE_NAME_NOMINAL_WEIGHT,
  ATTRIBUTE_NAME_PACKAGE_SIZE,
  ATTRIBUTE_NAME_PACKAGING_VARIANT,
  ATTRIBUTE_NAME_PATCH_SIZE,
  ATTRIBUTE_NAME_RESISTANCE,
  ATTRIBUTE_NAME_SIZE,
  ATTRIBUTE_NAME_TUBE_LENGTH,
} from './ATTRIBUTE_NAME'
import {
  ATTRIBUTE_SORT_WEIGHT_ADDITION,
  ATTRIBUTE_SORT_WEIGHT_ANIMAL_SIZE,
  ATTRIBUTE_SORT_WEIGHT_CANNULA_LENGTH,
  ATTRIBUTE_SORT_WEIGHT_CBD_CONTENT,
  ATTRIBUTE_SORT_WEIGHT_CONSISTENCY,
  ATTRIBUTE_SORT_WEIGHT_NOMINAL_WEIGHT,
  ATTRIBUTE_SORT_WEIGHT_PACKAGING_VARIANT,
  ATTRIBUTE_SORT_WEIGHT_PATCH_SIZE,
  ATTRIBUTE_SORT_WEIGHT_RESISTANCE,
  ATTRIBUTE_SORT_WEIGHT_SIZE,
  ATTRIBUTE_SORT_WEIGHT_TUBE_LENGTH,
} from './ATTRIBUTE_SORT_WEIGHT'
import {
  type AttributeGroups,
  type SelectionEntry,
} from './useProductPageAttributeSelectionControls'

const indexOfValue = (
  weightsArray: string[],
  value: string | undefined,
): number => {
  if (!value) {
    return 0
  }

  return weightsArray.indexOf(value)
}

/**
 * function that extracts numbers from string and returns them as number[]
 * example:
 * "14x15.5 PC" => [14, 15.5]
 * "5 Kg" => [5]
 */
const extractNumbersFromString = (value: string): number[] => {
  const numbers = value.match(/\d+\.?\d*/gu)
  if (!numbers) {
    return []
  }

  return numbers.map(Number).filter((it) => !Number.isNaN(it))
}

/**
 * returns true if string contains word X between digits without spaces
 * needed for e.g. package size multiplication
 * example:
 * "12x4 Mg" => true
 * "12 Mg" => false
 */
const shouldMultiply = (value: string): boolean => {
  return /\dx\d/gu.test(value)
}

/**
 * sanitizes package size value before it's weight can be calculated
 * example:
 * '12,4 Ml' => '12.4 Ml' // . is needed so 12.4 can be parsed as number in js
 */
const preparePackageSizeValueForNumberParsing = (argValue: string): string => {
  // System use ',' for decimal separation, and JS uses '.', 12,5 will result in 12.5
  return argValue.replaceAll(',', '.')
}

const TWO = 2

const getPackagingSizeSortWeight = (argValue: string | undefined): number => {
  if (!argValue) {
    return -1
  }

  const value = preparePackageSizeValueForNumberParsing(argValue)
  const numbers = extractNumbersFromString(value)

  // just one number
  if (numbers.length === 1) {
    return numbers[0]
  }

  // case for e.g. 12x4 Kg Mg we need to multiply 12 and 4
  if (shouldMultiply(value) && numbers.length === TWO) {
    return numbers[0] * numbers[1]
  }

  return -1
}

const getSelectionEntrySortCompareWeight = (
  selectionEntry: SelectionEntry,
): number | string => {
  const { attribute } = selectionEntry
  switch (attribute.variant_attribute) {
    case ATTRIBUTE_NAME_ADDITION: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_ADDITION, attribute.code)
    }
    case ATTRIBUTE_NAME_ANIMAL_SIZE: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_ANIMAL_SIZE, attribute.code)
    }
    case ATTRIBUTE_NAME_CANNULA_LENGTH: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_CANNULA_LENGTH, attribute.code)
    }
    case ATTRIBUTE_NAME_CBD_CONTENT: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_CBD_CONTENT, attribute.code)
    }
    case ATTRIBUTE_NAME_CONSISTENCY: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_CONSISTENCY, attribute.code)
    }
    case ATTRIBUTE_NAME_NOMINAL_WEIGHT: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_NOMINAL_WEIGHT, attribute.code)
    }
    case ATTRIBUTE_NAME_PACKAGE_SIZE: {
      return getPackagingSizeSortWeight(attribute.value)
    }
    case ATTRIBUTE_NAME_PACKAGING_VARIANT: {
      return indexOfValue(
        ATTRIBUTE_SORT_WEIGHT_PACKAGING_VARIANT,
        attribute.code,
      )
    }
    case ATTRIBUTE_NAME_PATCH_SIZE: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_PATCH_SIZE, attribute.code)
    }
    case ATTRIBUTE_NAME_RESISTANCE: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_RESISTANCE, attribute.code)
    }
    case ATTRIBUTE_NAME_SIZE: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_SIZE, attribute.code)
    }
    case ATTRIBUTE_NAME_TUBE_LENGTH: {
      return indexOfValue(ATTRIBUTE_SORT_WEIGHT_TUBE_LENGTH, attribute.code)
    }
    default: {
      return attribute.value ?? -1
    }
  }
}
/**
 * if an entry has buyable candidate and b entry does not, a goes first.
 * if both have or both don't have buyable candidate variants, sort by passed  comparison weights.
 */
const getSortWeightsHasBuyableCandidateFirst = ({
  firstEntryWeight,
  firstSelectionEntry,
  secondEntryWeight,
  secondSelectionEntry,
}: {
  firstEntryWeight: number | string
  firstSelectionEntry: SelectionEntry
  secondEntryWeight: number | string
  secondSelectionEntry: SelectionEntry
}): number => {
  const firstAvailabilityWeight =
    productPageGetAttributeSelectionEntryConditions(firstSelectionEntry)
      .candidateIsNotBuyable
      ? -1
      : 1
  const secondAvailabilityWeight =
    productPageGetAttributeSelectionEntryConditions(secondSelectionEntry)
      .candidateIsNotBuyable
      ? -1
      : 1

  // if some is not available, sort by availability
  if (firstAvailabilityWeight !== secondAvailabilityWeight) {
    return firstAvailabilityWeight - secondAvailabilityWeight
  }
  // both same availability sort by weight
  if (firstEntryWeight === secondEntryWeight) {
    return 0
  }

  return firstEntryWeight > secondEntryWeight ? 1 : -1
}

export const sortSelectionEntriesInGroups = (
  groups: AttributeGroups,
): AttributeGroups => {
  for (const group of Object.values(groups)) {
    if (group) {
      group.selectionEntries = group.selectionEntries.sort(
        (firstSelectionEntry, secondSelectionEntry) => {
          const firstEntryWeight =
            getSelectionEntrySortCompareWeight(firstSelectionEntry)
          const secondEntryWeight =
            getSelectionEntrySortCompareWeight(secondSelectionEntry)

          /**
           * edcase: for group that's shown with prices (aka "tile"), entries whose candidate is "not buyable (has no stock)" shall go to end.
           */
          if (group.shouldShowPricesOnSelections) {
            return getSortWeightsHasBuyableCandidateFirst({
              firstEntryWeight,
              firstSelectionEntry,
              secondEntryWeight,
              secondSelectionEntry,
            })
          }

          if (firstEntryWeight === secondEntryWeight) {
            return 0
          }

          return firstEntryWeight > secondEntryWeight ? 1 : -1
        },
      )
    }
  }

  return groups
}
